import React from "react";
import styled from "@emotion/styled";
import { POCItemsDocumentAnswer } from "../../messages/PatientDocuments";
import { TaskWhenToPerform } from "../../messages/PlanOfCare";
import { parseDayOfWeek } from "../../MessageRevivers";
import { translate } from "../../translate";
import { STRINGS } from "../../strings";
import Flex from "../Generic/Flex";

interface POCItemTaskBodyProps {
  index: number;
  currentAnswer: POCItemsDocumentAnswer | undefined;
  readonly: boolean;
  onWhenToPerformChange: (index: number, whenToPerform: TaskWhenToPerform) => void;
  onFrequencyChange: (
    index: number,
    frequency: number | null,
    visitsInAWeek: number | null
  ) => void;
  onNotesChange: (index: number, notes: string) => void;
}

export function POCItemTaskBody({
  index,
  currentAnswer,
  readonly,
  onWhenToPerformChange,
  onFrequencyChange,
  onNotesChange,
}: POCItemTaskBodyProps) {
  const handleDayClick = (day: string, isSelected: boolean) => {
    if (readonly) return;
    if (currentAnswer?.attributes.type !== "OnDaysOfWeek") return;
    const newDays = isSelected
      ? currentAnswer.attributes.days?.filter((d) => d !== parseDayOfWeek(day))
      : [...(currentAnswer.attributes.days || []), parseDayOfWeek(day)];
    onWhenToPerformChange(index, {
      type: "OnDaysOfWeek",
      days: newDays,
      frequency: currentAnswer.attributes.frequency,
    });
  };

  const handleFrequencySelect = (value: string | null, isVisitsPerWeek: boolean) => {
    if (currentAnswer?.attributes.type !== "OnDaysOfWeek") return;
    const numValue = value === null || value === "" ? null : parseInt(value);
    onFrequencyChange(
      index,
      isVisitsPerWeek ? currentAnswer.attributes.frequency?.frequency ?? null : numValue,
      isVisitsPerWeek ? numValue : currentAnswer.attributes.frequency?.visitsInAWeek ?? null
    );
  };

  return (
    <BodyContainer>
      <TypeContainer>
        <div className="checkbox">
          <input
            type="checkbox"
            className="checkbox__input"
            checked={currentAnswer?.attributes.type === "AsRequestedByPatient"}
            onChange={() => onWhenToPerformChange(index, { type: "AsRequestedByPatient" })}
            disabled={readonly}
          />
          <span className="checkbox__checkmark" />
        </div>
        {translate(STRINGS.AS_REQUESTED_BY_THE_PATIENT)}
      </TypeContainer>

      <TypeContainer>
        <div className="checkbox">
          <input
            type="checkbox"
            className="checkbox__input"
            checked={currentAnswer?.attributes.type === "EveryVisit"}
            onChange={() => onWhenToPerformChange(index, { type: "EveryVisit" })}
            disabled={readonly}
          />
          <span className="checkbox__checkmark" />
        </div>
        {translate(STRINGS.EVERY_VISIT)}
      </TypeContainer>

      <TypeContainer>
        <div className="checkbox">
          <input
            type="checkbox"
            className="checkbox__input"
            checked={currentAnswer?.attributes.type === "OnDaysOfWeek"}
            onChange={() => onWhenToPerformChange(index, { type: "OnDaysOfWeek", days: [] })}
            disabled={readonly}
          />
          <span className="checkbox__checkmark" />
        </div>
        {translate(STRINGS.DAYS_OF_WEEK)}
      </TypeContainer>

      {currentAnswer?.attributes.type === "OnDaysOfWeek" && (
        <>
          <DaysTitle>{translate(STRINGS.SPECIFIC_DAYS)}</DaysTitle>
          <DaysContainer>
            {["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"].map(
              (day) => {
                const isSelected =
                  currentAnswer.attributes.type === "OnDaysOfWeek" &&
                  Boolean(currentAnswer.attributes.days?.includes(parseDayOfWeek(day)));
                return (
                  <DayContainer
                    key={day}
                    selected={isSelected}
                    onClick={() => handleDayClick(day, isSelected)}
                  >
                    {day.slice(0, 2)}
                  </DayContainer>
                );
              }
            )}
          </DaysContainer>

          <FrequencyContainer>
            <Flex direction="row" gap={16} alignItems="center">
              <FrequencyInput>
                <label>{translate(STRINGS.FREQUENCY)}:</label>
                <Select
                  value={currentAnswer.attributes.frequency?.frequency ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleFrequencySelect(e.target.value, false)
                  }
                  disabled={readonly}
                >
                  <option value="" />
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </Select>
              </FrequencyInput>
              <FrequencyInput>
                <label>{translate(STRINGS.VISITS_PER_WEEK)}:</label>
                <Select
                  value={currentAnswer.attributes.frequency?.visitsInAWeek ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleFrequencySelect(e.target.value, true)
                  }
                  disabled={readonly}
                >
                  <option value="" />
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </Select>
              </FrequencyInput>
            </Flex>
          </FrequencyContainer>
        </>
      )}

      <NotesContainer>
        <NotesLabel>{translate(STRINGS.NOTES)}:</NotesLabel>
        <NotesTextArea
          value={currentAnswer?.notes ?? ""}
          onChange={(e) => onNotesChange(index, e.target.value)}
          placeholder={translate(STRINGS.NOTES)}
          disabled={readonly}
        />
      </NotesContainer>
    </BodyContainer>
  );
}

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid lightgrey;
  padding-top: 10px;
  margin-top: 8px;
`;

const TypeContainer = styled.div`
  margin: 4px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const DaysContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
  justify-content: space-around;
  align-items: center;
`;

const DayContainer = styled.div<{ selected?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: ${(props) => (props.selected === true ? "2px solid #0076ff" : "1px solid #DADADA")};
  border-radius: 20px;
  width: 36px;
  height: 36px;
  font-size: 15px;
  box-sizing: border-box;
  color: ${(props) => (props.selected === true ? "#0076ff" : "inherit")};
  font-weight: ${(props) => (props.selected === true ? "600" : "normal")};
  cursor: pointer;
`;

const DaysTitle = styled.div`
  font-weight: 600;
  margin-top: 14px;
  margin-bottom: 8px;
`;

const Select = styled.select`
  margin-left: 10px;
  margin-right: 10px;
  border-radius: 5px;
  padding: 8px;
  border: 1px solid #cacdd1;
  font-size: 14px;
`;

const FrequencyContainer = styled.div`
  margin-top: 16px;
`;

const FrequencyInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NotesContainer = styled.div`
  box-sizing: border-box;
  margin-top: 16px;
`;

const NotesLabel = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const NotesTextArea = styled.textarea`
  width: 80%;
  min-height: 20px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #cacdd1;
  font-size: 14px;
  resize: none;
`;
